import { isRevenueCatAnonymousAppUserId } from "./app-user-id";
import type { PackageType } from "./config";

export const PENDING_WEB_PURCHASE_STORAGE_KEY = "chickytutor:revenuecat:web-purchase:v1";
export const ANONYMOUS_PURCHASE_ACTIVATION_PATH = "/activate-purchase";

const PENDING_PURCHASE_TTL_MS = 8 * 24 * 60 * 60 * 1000;
const REVENUECAT_REDEMPTION_PROTOCOL = "rc-8ea0f9ad48:";

export type PendingWebPurchase = {
  anonymousAppUserId: string;
  redeemUrl: string | null;
  packageType: PackageType;
  purchasedAtMs: number;
};

function parsePendingWebPurchase(value: string | null, nowMs: number): PendingWebPurchase | null {
  try {
    const parsed = JSON.parse(value || "null") as Partial<PendingWebPurchase> | null;
    if (
      !parsed ||
      !isRevenueCatAnonymousAppUserId(parsed.anonymousAppUserId) ||
      (parsed.redeemUrl !== null && !isChickyTutorRedemptionUrl(parsed.redeemUrl)) ||
      (parsed.packageType !== "weekly" &&
        parsed.packageType !== "monthly" &&
        parsed.packageType !== "annual") ||
      typeof parsed.purchasedAtMs !== "number" ||
      nowMs < parsed.purchasedAtMs ||
      nowMs - parsed.purchasedAtMs > PENDING_PURCHASE_TTL_MS
    ) {
      return null;
    }

    return parsed as PendingWebPurchase;
  } catch {
    return null;
  }
}

export function isChickyTutorRedemptionUrl(value: unknown): value is string {
  if (typeof value !== "string" || !value.trim()) return false;
  try {
    const url = new URL(value);
    return (
      url.protocol === REVENUECAT_REDEMPTION_PROTOCOL &&
      url.hostname === "redeem_web_purchase" &&
      Boolean(url.searchParams.get("redemption_token"))
    );
  } catch {
    return false;
  }
}

export function storePendingWebPurchase(
  purchase: PendingWebPurchase,
  crossTabStorage: Storage | null = typeof window === "undefined" ? null : window.localStorage,
  tabStorage: Storage | null = typeof window === "undefined" ? null : window.sessionStorage
): boolean {
  if (!isRevenueCatAnonymousAppUserId(purchase.anonymousAppUserId)) return false;
  if (purchase.redeemUrl && !isChickyTutorRedemptionUrl(purchase.redeemUrl)) return false;

  let stored = false;
  try {
    crossTabStorage?.setItem(
      PENDING_WEB_PURCHASE_STORAGE_KEY,
      JSON.stringify({ ...purchase, redeemUrl: null })
    );
    stored = Boolean(crossTabStorage);
  } catch {
    // The original tab can still retain the purchase if cross-tab storage is unavailable.
  }

  try {
    if (tabStorage && tabStorage !== crossTabStorage) {
      tabStorage.setItem(PENDING_WEB_PURCHASE_STORAGE_KEY, JSON.stringify(purchase));
      stored = true;
    }
  } catch {
    // Cross-tab activation remains available without the provider redemption URL.
  }

  return stored;
}

export function readPendingWebPurchase(
  crossTabStorage: Storage | null = typeof window === "undefined" ? null : window.localStorage,
  nowMs = Date.now(),
  tabStorage: Storage | null = typeof window === "undefined" ? null : window.sessionStorage
): PendingWebPurchase | null {
  try {
    if (tabStorage && tabStorage !== crossTabStorage) {
      const tabPurchase = parsePendingWebPurchase(
        tabStorage.getItem(PENDING_WEB_PURCHASE_STORAGE_KEY),
        nowMs
      );
      if (tabPurchase) return tabPurchase;
    }
  } catch {
    // Fall through to the token-free cross-tab record.
  }

  try {
    return parsePendingWebPurchase(
      crossTabStorage?.getItem(PENDING_WEB_PURCHASE_STORAGE_KEY) ?? null,
      nowMs
    );
  } catch {
    return null;
  }
}

export function clearPendingWebPurchase(
  crossTabStorage: Storage | null = typeof window === "undefined" ? null : window.localStorage,
  tabStorage: Storage | null = typeof window === "undefined" ? null : window.sessionStorage
): void {
  try {
    crossTabStorage?.removeItem(PENDING_WEB_PURCHASE_STORAGE_KEY);
  } catch {
    // A stale activation record must never block the customer.
  }
  try {
    if (tabStorage !== crossTabStorage) {
      tabStorage?.removeItem(PENDING_WEB_PURCHASE_STORAGE_KEY);
    }
  } catch {
    // A stale tab-local redemption URL must never block the customer.
  }
}

export function isAnonymousPurchaseActivationPath(value: string | null | undefined): boolean {
  if (!value?.startsWith("/") || value.startsWith("//")) return false;
  try {
    const url = new URL(value, "https://www.chickytutor.com");
    return url.pathname === ANONYMOUS_PURCHASE_ACTIVATION_PATH;
  } catch {
    return false;
  }
}
